import { db } from '../db/db';

interface ServerState {
  isRunning: boolean;
  port: number;
  connectedClients: number;
  serverIP: string;
}

let serverState: ServerState = {
  isRunning: false,
  port: 7743,
  connectedClients: 0,
  serverIP: '',
};

let abortController: AbortController | null = null;
let broadcastChannel: BroadcastChannel | null = null;

export function getServerState(): ServerState {
  return { ...serverState };
}

export async function getLocalIP(): Promise<string> {
  // Method 1: Try fetching from our own server (most reliable)
  try {
    const res = await fetch(`http://localhost:${serverState.port}/api/ping`, { signal: AbortSignal.timeout(2000) });
    const data = await res.json();
    if (data.serverIP && data.serverIP !== 'localhost') return data.serverIP;
  } catch {}

  // Method 2: WebRTC (may return .local mDNS on modern browsers)
  try {
    const rtc = new RTCPeerConnection({ iceServers: [] });
    rtc.createDataChannel('');
    const offer = await rtc.createOffer();
    await rtc.setLocalDescription(offer);
    return new Promise<string>((resolve) => {
      let found = false;
      rtc.onicecandidate = (e) => {
        if (found) return;
        if (e.candidate) {
          const parts = e.candidate.candidate.split(' ');
          const ip = parts[4];
          // Only accept numeric IPs (192.168.x.x), reject .local mDNS
          if (ip && /^\d+\.\d+\.\d+\.\d+$/.test(ip) && ip !== '0.0.0.0') {
            found = true;
            rtc.close();
            resolve(ip);
          }
        }
      };
      setTimeout(() => { if (!found) { rtc.close(); resolve(''); } }, 3000);
    });
  } catch {
    return '';
  }
}

export async function startMultiUserServer(port = 7743): Promise<{ success: boolean; message: string }> {
  if (serverState.isRunning) return { success: false, message: 'Server already running' };

  try {
    const ip = await getLocalIP();
    serverState.serverIP = ip;
    serverState.port = port;
    serverState.isRunning = true;

    broadcastChannel = new BroadcastChannel('aarthika-multi-user');
    broadcastChannel.onmessage = (event) => {
      if (event.data.type === 'CLIENT_CONNECTED') serverState.connectedClients++;
      if (event.data.type === 'CLIENT_DISCONNECTED') serverState.connectedClients = Math.max(0, serverState.connectedClients - 1);
    };

    return { success: true, message: `Server started on ${ip}:${port}` };
  } catch (e: any) {
    serverState.isRunning = false;
    return { success: false, message: `Failed to start: ${e.message}` };
  }
}

export function stopMultiUserServer(): { success: boolean; message: string } {
  if (!serverState.isRunning) return { success: false, message: 'Server not running' };

  if (abortController) { abortController.abort(); abortController = null; }
  if (broadcastChannel) { broadcastChannel.close(); broadcastChannel = null; }

  serverState.isRunning = false;
  serverState.connectedClients = 0;
  serverState.serverIP = '';

  return { success: true, message: 'Server stopped' };
}

// API handler — processes requests from clients
export async function handleAPIRequest(method: string, path: string, body?: any): Promise<{ status: number; data: any }> {
  const segments = path.split('/').filter(Boolean);
  const resource = segments[0];
  const id = segments[1] ? Number(segments[1]) : null;

  try {
    switch (resource) {
      case 'companies': {
        if (method === 'GET' && !id) {
          const data = await db.companies.toArray();
          return { status: 200, data };
        }
        if (method === 'GET' && id) {
          const data = await db.companies.get(id);
          return data ? { status: 200, data } : { status: 404, data: { error: 'Not found' } };
        }
        break;
      }

      case 'groups': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.groups.where('companyId').equals(companyId).toArray()
            : await db.groups.toArray();
          return { status: 200, data };
        }
        if (method === 'POST' && body) {
          const id = await db.groups.add(body);
          return { status: 201, data: { id } };
        }
        if (method === 'PUT' && id && body) {
          await db.groups.update(id, body);
          return { status: 200, data: { success: true } };
        }
        break;
      }

      case 'ledgers': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.ledgers.where('companyId').equals(companyId).toArray()
            : await db.ledgers.toArray();
          return { status: 200, data };
        }
        if (method === 'POST' && body) {
          const id = await db.ledgers.add(body);
          return { status: 201, data: { id } };
        }
        if (method === 'PUT' && id && body) {
          await db.ledgers.update(id, body);
          return { status: 200, data: { success: true } };
        }
        break;
      }

      case 'vouchers': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.vouchers.where('companyId').equals(companyId).filter(v => !v.deleted).toArray()
            : await db.vouchers.filter(v => !v.deleted).toArray();
          return { status: 200, data };
        }
        if (method === 'POST' && body) {
          const id = await db.vouchers.add(body);
          return { status: 201, data: { id } };
        }
        if (method === 'PUT' && id && body) {
          await db.vouchers.update(id, body);
          return { status: 200, data: { success: true } };
        }
        break;
      }

      case 'voucherEntries': {
        if (method === 'GET') {
          const voucherId = body?.voucherId;
          const data = voucherId
            ? await db.voucherEntries.where('voucherId').equals(voucherId).toArray()
            : await db.voucherEntries.toArray();
          return { status: 200, data };
        }
        if (method === 'POST' && body) {
          const id = await db.voucherEntries.add(body);
          return { status: 201, data: { id } };
        }
        break;
      }

      case 'inventoryEntries': {
        if (method === 'GET') {
          const voucherId = body?.voucherId;
          const data = voucherId
            ? await db.inventoryEntries.where('voucherId').equals(voucherId).toArray()
            : await db.inventoryEntries.toArray();
          return { status: 200, data };
        }
        break;
      }

      case 'stockItems': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.stockItems.where('companyId').equals(companyId).toArray()
            : await db.stockItems.toArray();
          return { status: 200, data };
        }
        if (method === 'POST' && body) {
          const id = await db.stockItems.add(body);
          return { status: 201, data: { id } };
        }
        break;
      }

      case 'stockGroups': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.stockGroups.where('companyId').equals(companyId).toArray()
            : await db.stockGroups.toArray();
          return { status: 200, data };
        }
        break;
      }

      case 'units': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.units.where('companyId').equals(companyId).toArray()
            : await db.units.toArray();
          return { status: 200, data };
        }
        break;
      }

      case 'voucherTypes': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.voucherTypes.where('companyId').equals(companyId).toArray()
            : await db.voucherTypes.toArray();
          return { status: 200, data };
        }
        break;
      }

      case 'employees': {
        if (method === 'GET') {
          const companyId = body?.companyId;
          const data = companyId
            ? await db.employees.where('companyId').equals(companyId).toArray()
            : await db.employees.toArray();
          return { status: 200, data };
        }
        break;
      }

      case 'ping': {
        return { status: 200, data: { pong: true, timestamp: Date.now(), clients: serverState.connectedClients } };
      }

      default:
        return { status: 404, data: { error: `Unknown resource: ${resource}` } };
    }

    return { status: 405, data: { error: 'Method not allowed' } };
  } catch (e: any) {
    return { status: 500, data: { error: e.message } };
  }
}
